import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import WPTabs from "../../packages/WPTabs";

import { getNavigationState } from "./PageNavigation.utils";

import {
  NavigationActiveDotIcon,
  NavigationActiveNotchIcon,
} from "../../assets/icons";

import { combineClassNames } from "../../utils/appUtils";

import "./PageNavigation.scss";

const getPagePath = (page) => page.path ?? page.aliasPath;

const PageNavigation = ({ className, activePageKey, onPageChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { navigationPages, isControlled, activePageIndex } = getNavigationState(
    {
      pathname: location.pathname,
      activePageKey,
    },
  );

  if (navigationPages.length === 0) {
    return null;
  }

  const isPageDisabled = (page, index) =>
    Boolean(page.disabled) ||
    (!isControlled && !getPagePath(page)) ||
    index > activePageIndex;

  const handleStepClick = (index) => {
    const page = navigationPages[index];
    if (isPageDisabled(page, index) || index === activePageIndex) {
      return;
    }
    if (isControlled) {
      onPageChange?.(page, index);
      return;
    }

    const params = new URLSearchParams(location.search);
    Object.entries(page.queryParams || {}).forEach(([key, value]) => {
      if (value == null || value === "") {
        params.delete(key);
      } else {
        params.set(key, String(value));
      }
    });
    navigate({ pathname: getPagePath(page), search: params.toString() });
  };

  const tabs = navigationPages.map((page, index) => ({
    id: page.key,
    disabled: isPageDisabled(page, index),
    minWidth: page.minWidth,
    ariaCurrent: index === activePageIndex ? "step" : undefined,
    title: (
      <span
        className={combineClassNames(
          "page-navigation__item-content",
          index < activePageIndex && "page-navigation__item-content--completed",
        )}
      >
        <span className="page-navigation__index" aria-hidden="true">
          {index + 1}
        </span>
        <span className="page-navigation__label">
          {page.label || t(page.labelKey)}
        </span>
        {index === activePageIndex && (
          <span className="page-navigation__marker" aria-hidden="true">
            <NavigationActiveNotchIcon className="page-navigation__notch" />
            <NavigationActiveDotIcon className="page-navigation__dot" />
          </span>
        )}
      </span>
    ),
  }));

  return (
    <WPTabs
      tabs={tabs}
      className={combineClassNames("page-navigation", className)}
      activeTabIndex={activePageIndex}
      onTabChange={handleStepClick}
    />
  );
};

export default PageNavigation;
